import React, { useRef, useEffect } from 'react';

const POINTS = 12;
const DIST = 120;
const RADIUS = 2.2;
const LINE_COLOR = '#bdbdbd'; // gris
const POINT_COLOR = '#d09c5a'; // dorado

function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

function createPoints(width, height) {
  return Array.from({ length: POINTS }, () => ({
    x: randomBetween(0, width),
    y: randomBetween(0, height),
    vx: randomBetween(-0.3, 0.3),
    vy: randomBetween(-0.3, 0.3),
  }));
}

export default function NetworkBackground() {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const mouse = useRef({ x: null, y: null });
  const points = useRef([]);

  // NUEVO: función para saber si estamos en móvil
  function isMobile() {
    return window.innerWidth <= 1024;
  }

  // NUEVO: función para saber si un punto está dentro del área del logo móvil
  function isInLogoArea(x, y) {
    if (!isMobile()) return false;
    // Tamaño del logo móvil: 135px (>=769px), 108px (<=768px), más un margen
    const margin = 16;
    let logoWidth = window.innerWidth <= 768 ? 108 : 135;
    let logoHeight = logoWidth * 1; // asumiendo logo cuadrado
    return x < logoWidth + margin && y < logoHeight + margin;
  }

  // NUEVO: función para saber si un punto está dentro del área del menú móvil
  function isInMenuArea(x, y) {
    if (!isMobile()) return false;
    // El menú está en la esquina superior derecha
    // Suponiendo altura de barra móvil: 70px, ancho del botón: 110px, margen derecho: 0
    const menuWidth = 110; // px
    const menuHeight = 70; // px
    return x > window.innerWidth - menuWidth && y < menuHeight;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;
    points.current = createPoints(width, height);

    function draw() {
      ctx.clearRect(0, 0, width, height);
      // Draw lines
      for (let i = 0; i < POINTS; i++) {
        for (let j = i + 1; j < POINTS; j++) {
          const p1 = points.current[i];
          const p2 = points.current[j];
          // NUEVO: no dibujar líneas si alguno está en el área del logo móvil o menú móvil
          if (isInLogoArea(p1.x, p1.y) || isInLogoArea(p2.x, p2.y) || isInMenuArea(p1.x, p1.y) || isInMenuArea(p2.x, p2.y)) continue;
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < DIST) {
            ctx.strokeStyle = LINE_COLOR;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      // Draw points
      for (let i = 0; i < POINTS; i++) {
        const p = points.current[i];
        // NUEVO: no dibujar puntos en el área del logo móvil o menú móvil
        if (isInLogoArea(p.x, p.y) || isInMenuArea(p.x, p.y)) continue;
        ctx.beginPath();
        ctx.arc(p.x, p.y, RADIUS, 0, 2 * Math.PI);
        ctx.fillStyle = POINT_COLOR;
        ctx.fill();
      }
      // Draw lines to mouse
      if (mouse.current.x !== null && mouse.current.y !== null) {
        for (let i = 0; i < POINTS; i++) {
          const p = points.current[i];
          // NUEVO: no dibujar líneas si el punto está en el área del logo móvil o menú móvil
          if (isInLogoArea(p.x, p.y) || isInMenuArea(p.x, p.y)) continue;
          const dx = p.x - mouse.current.x;
          const dy = p.y - mouse.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < DIST) {
            ctx.strokeStyle = LINE_COLOR;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.current.x, mouse.current.y);
            ctx.stroke();
          }
        }
      }
    }

    function animate() {
      for (let i = 0; i < POINTS; i++) {
        let p = points.current[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }
      draw();
      animationRef.current = requestAnimationFrame(animate);
    }

    animate();

    function handleMouse(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = e.clientX - rect.left;
      mouse.current.y = e.clientY - rect.top;
    }
    function handleMouseOut() {
      mouse.current.x = null;
      mouse.current.y = null;
    }
    canvas.addEventListener('mousemove', handleMouse);
    canvas.addEventListener('mouseleave', handleMouseOut);

    function handleResize() {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      points.current = createPoints(width, height);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      canvas.removeEventListener('mousemove', handleMouse);
      canvas.removeEventListener('mouseleave', handleMouseOut);
      window.removeEventListener('resize', handleResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
} 